import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

export type TurnoutMatrix = {
  sabilWithRsvp: number;
  sabilWithoutRsvp: number;
  mehmaanWithRsvp: number;
  mehmaanWithoutRsvp: number;
};

export type AttendanceTurnoutData = {
  totalScannedAttendees: number;
  male?: number;
  female?: number;
  childrenUnder10?: number;
  withRsvp?: number;
  withoutRsvp?: number;
  sabilHolders?: number;
  mehmaan?: number;
  matrix?: TurnoutMatrix;
  totalRsvpExpected?: number;
  rsvpAttended?: number;
  rsvpNoShow?: number;
};

type Props = {
  scanned: AttendanceTurnoutData;
  totalRsvpAttendees?: number;
  onExport?: () => void;
  isExporting?: boolean;
};

function pct(numerator: number, denominator: number): string {
  if (!denominator || denominator <= 0) return "0.0%";
  return `${((numerator / denominator) * 100).toFixed(1)}%`;
}

function StatTile({
  title,
  value,
  subtitle,
  icon: Icon,
  accentColor,
}: {
  title: string;
  value: string | number;
  subtitle: string;
  icon: typeof PeopleAltOutlinedIcon;
  accentColor?: string;
}) {
  return (
    <Card
      variant="outlined"
      sx={{ p: 2, height: "100%", borderRadius: 2, bgcolor: "background.paper" }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            textTransform="uppercase"
            letterSpacing={0.5}
          >
            {title}
          </Typography>
          <Typography
            variant="h5"
            fontWeight={700}
            color={accentColor ?? "text.primary"}
            sx={{ my: 0.5 }}
          >
            {value}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {subtitle}
          </Typography>
        </Box>
        <Box
          sx={{
            p: 1,
            borderRadius: 1.5,
            bgcolor: accentColor ? `${accentColor}14` : "action.hover",
            color: accentColor ?? "primary.main",
            display: "flex",
          }}
        >
          <Icon fontSize="small" />
        </Box>
      </Stack>
    </Card>
  );
}

export default function MiqaatAttendanceAuditCard({
  scanned,
  totalRsvpAttendees,
  onExport,
  isExporting = false,
}: Props) {
  const totalScanned = scanned.totalScannedAttendees ?? 0;
  if (totalScanned <= 0) return null;

  const withRsvp = scanned.withRsvp ?? 0;
  const withoutRsvp = scanned.withoutRsvp ?? 0;
  const sabilHolders = scanned.sabilHolders ?? 0;
  const mehmaan = scanned.mehmaan ?? 0;

  const matrix = scanned.matrix ?? {
    sabilWithRsvp: 0,
    sabilWithoutRsvp: 0,
    mehmaanWithRsvp: 0,
    mehmaanWithoutRsvp: 0,
  };

  const expectedRsvp = scanned.totalRsvpExpected ?? totalRsvpAttendees ?? 0;
  const rsvpAttended = scanned.rsvpAttended ?? withRsvp;
  const rsvpNoShow = scanned.rsvpNoShow ?? Math.max(0, expectedRsvp - rsvpAttended);

  // Segment colors
  const COLOR_SABIL_RSVP = "#2e7d32"; // Dark Green
  const COLOR_SABIL_WALKIN = "#ed6c02"; // Orange
  const COLOR_MEHMAAN_RSVP = "#0288d1"; // Light Blue
  const COLOR_MEHMAAN_WALKIN = "#7b1fa2"; // Purple

  const segments = [
    {
      label: "Sabil w/ RSVP",
      count: matrix.sabilWithRsvp,
      color: COLOR_SABIL_RSVP,
      description: "Pre-registered local sabil holders",
    },
    {
      label: "Sabil Walk-in",
      count: matrix.sabilWithoutRsvp,
      color: COLOR_SABIL_WALKIN,
      description: "Unannounced local sabil holders",
    },
    {
      label: "Mehmaan w/ RSVP",
      count: matrix.mehmaanWithRsvp,
      color: COLOR_MEHMAAN_RSVP,
      description: "Pre-registered visiting guests",
    },
    {
      label: "Mehmaan Walk-in",
      count: matrix.mehmaanWithoutRsvp,
      color: COLOR_MEHMAAN_WALKIN,
      description: "Unannounced visiting guests",
    },
  ];

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", mt: 2.5 }}>
      <Box
        sx={{ px: 2.5, py: 2, bgcolor: "action.hover", borderBottom: 1, borderColor: "divider" }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ sm: "center" }}
          spacing={1.5}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color="text.primary">
              Post-Attendance Turnout Audit
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Breakdown of actual scanned attendees by RSVP status and Sabil vs. Mehmaan
              classification.
            </Typography>
          </Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}
          >
            {onExport && (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={
                  isExporting ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <FileDownloadOutlinedIcon fontSize="small" />
                  )
                }
                disabled={isExporting || totalScanned === 0}
                onClick={onExport}
                sx={{
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: 1.5,
                  fontSize: "0.8125rem",
                  px: 1.5,
                }}
              >
                {isExporting ? "Exporting..." : "Export to Excel"}
              </Button>
            )}
            <Chip
              size="small"
              icon={<FactCheckOutlinedIcon />}
              label={`${totalScanned} Total Scanned`}
              color="primary"
              variant="filled"
              sx={{ fontWeight: 600 }}
            />
          </Stack>
        </Stack>
      </Box>

      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        {/* Hero KPI Summary Row */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatTile
              title="RSVP Compliance"
              value={pct(withRsvp, totalScanned)}
              subtitle={`${withRsvp} with RSVP · ${withoutRsvp} walk-ins`}
              icon={HowToRegIcon}
              accentColor="#2e7d32"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatTile
              title="Sabil Holders"
              value={`${sabilHolders} (${pct(sabilHolders, totalScanned)})`}
              subtitle={`${matrix.sabilWithRsvp} RSVP · ${matrix.sabilWithoutRsvp} walk-in`}
              icon={PeopleAltOutlinedIcon}
              accentColor="#1976d2"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatTile
              title="Mehmaan / Guests"
              value={`${mehmaan} (${pct(mehmaan, totalScanned)})`}
              subtitle={`${matrix.mehmaanWithRsvp} RSVP · ${matrix.mehmaanWithoutRsvp} walk-in`}
              icon={AssessmentOutlinedIcon}
              accentColor="#7b1fa2"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatTile
              title="RSVP Fulfillment"
              value={expectedRsvp > 0 ? pct(rsvpAttended, expectedRsvp) : "—"}
              subtitle={`${rsvpNoShow} no-shows of ${expectedRsvp} expected`}
              icon={FactCheckOutlinedIcon}
              accentColor={rsvpNoShow > 0 ? "#ed6c02" : "#2e7d32"}
            />
          </Grid>
        </Grid>

        {/* 2x2 Turnout Cross-Tabulation Matrix */}
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, letterSpacing: 0.3 }}>
          2×2 Turnout Cross-Tabulation Matrix
        </Typography>
        <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 1.5, mb: 3 }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: "action.hover" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Attendee Category</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: "success.main" }}>
                  With RSVP (✓)
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: "warning.main" }}>
                  Without RSVP (Walk-in)
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Total Turnout
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow hover>
                <TableCell sx={{ fontWeight: 600 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#1976d2" }} />
                    <Typography variant="body2" fontWeight={600}>
                      Sabil Holders (Local)
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" fontWeight={600}>
                    {matrix.sabilWithRsvp}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({pct(matrix.sabilWithRsvp, sabilHolders || 1)} of Sabil)
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" fontWeight={600}>
                    {matrix.sabilWithoutRsvp}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({pct(matrix.sabilWithoutRsvp, sabilHolders || 1)} of Sabil)
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={700}>
                    {sabilHolders}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({pct(sabilHolders, totalScanned)} of all)
                  </Typography>
                </TableCell>
              </TableRow>

              <TableRow hover>
                <TableCell sx={{ fontWeight: 600 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#7b1fa2" }} />
                    <Typography variant="body2" fontWeight={600}>
                      Mehmaan / Guests
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" fontWeight={600}>
                    {matrix.mehmaanWithRsvp}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({pct(matrix.mehmaanWithRsvp, mehmaan || 1)} of Mehmaan)
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" fontWeight={600}>
                    {matrix.mehmaanWithoutRsvp}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({pct(matrix.mehmaanWithoutRsvp, mehmaan || 1)} of Mehmaan)
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={700}>
                    {mehmaan}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({pct(mehmaan, totalScanned)} of all)
                  </Typography>
                </TableCell>
              </TableRow>

              <TableRow sx={{ bgcolor: "action.selected" }}>
                <TableCell sx={{ fontWeight: 700 }}>Total Scanned</TableCell>
                <TableCell align="center">
                  <Typography variant="body2" fontWeight={700} color="success.main">
                    {withRsvp}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({pct(withRsvp, totalScanned)})
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" fontWeight={700} color="warning.main">
                    {withoutRsvp}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({pct(withoutRsvp, totalScanned)})
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={800}>
                    {totalScanned}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    (100%)
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Visual Segmented Composition Bar */}
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, letterSpacing: 0.3 }}>
          Turnout Composition (4-way Split)
        </Typography>
        <Box
          sx={{
            display: "flex",
            height: 24,
            borderRadius: 1.5,
            overflow: "hidden",
            bgcolor: "action.hover",
            mb: 1.5,
          }}
        >
          {segments.map((seg) => {
            const widthPct = (seg.count / (totalScanned || 1)) * 100;
            if (widthPct <= 0) return null;
            return (
              <Tooltip
                key={seg.label}
                title={`${seg.label}: ${seg.count} (${widthPct.toFixed(1)}%) — ${seg.description}`}
                arrow
              >
                <Box
                  sx={{
                    width: `${widthPct}%`,
                    bgcolor: seg.color,
                    transition: "width 0.4s ease",
                    "&:hover": { opacity: 0.85, cursor: "pointer" },
                  }}
                />
              </Tooltip>
            );
          })}
        </Box>

        {/* Legend */}
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {segments.map((seg) => (
            <Grid key={seg.label} size={{ xs: 6, sm: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: 0.5,
                    bgcolor: seg.color,
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" fontWeight={600} display="block" noWrap>
                    {seg.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {seg.count} ({pct(seg.count, totalScanned)})
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          ))}
        </Grid>

        {expectedRsvp > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            {/* RSVP Conversion Funnel */}
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, letterSpacing: 0.3 }}>
              RSVP Conversion &amp; Fulfillment
            </Typography>
            <Stack spacing={1}>
              <Box>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    RSVPs Pre-Registered ({expectedRsvp}) → Attended: {rsvpAttended} (
                    {pct(rsvpAttended, expectedRsvp)}) · No-Shows: {rsvpNoShow} (
                    {pct(rsvpNoShow, expectedRsvp)})
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    display: "flex",
                    height: 12,
                    borderRadius: 1,
                    overflow: "hidden",
                    bgcolor: "action.hover",
                  }}
                >
                  <Box
                    sx={{ width: `${(rsvpAttended / expectedRsvp) * 100}%`, bgcolor: "#2e7d32" }}
                  />
                  <Box
                    sx={{ width: `${(rsvpNoShow / expectedRsvp) * 100}%`, bgcolor: "#ed6c02" }}
                  />
                </Box>
              </Box>
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
}
